<script>
  import { format } from 'svelte-i18n';
  import { Button, Input, InputGroup, Spinner, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';

  import { AssetsService } from '@client/services/assets';

  import { generateUUID } from '@common/utils/strings';

  /**
   * @typedef {Object} ImageAsset
   * @property {string} hash
   * @property {string} imageurl
   * @property {number} filesize
   * @property {string} filename
   * @property {Object} metadata
   * @property {string} metadata.mimetype
   * @property {boolean} inline
   * @property {string} mimetype
   * @property {string} rulename
   */

  const { addNotification } = getNotificationsContext();
  const hideMobileLabel = 'd-none d-sm-inline d-md-none d-xl-inline';
  const id = `upload-${generateUUID()}`;

  /**
   * Additional CSS class name for the component
   * @type {string}
   * @default ''
   */
  let className = '';
  export { className as class };

  /**
   * Indicates if an upload is currently in progress.
   * @type {boolean}
   */
  export let uploading = false;

  /**
   * Callback function triggered when the selected image is uploaded or is removed.
   * @type {function(ImageAsset | undefined): void}
   */
  export let onChange = () => {};

  /**
   * Callback function triggered when the selected image is picked before upload.
   * @type {function(ImageAsset | undefined): void}
   */
  export let onPreview = () => {};

  /**
   * URL of the asset, or preview URL if not yet uploaded.
   * @type {string | undefined}
   */
  export let url;

  /** @type {HTMLInputElement} */
  let fileInput;

  /** @type {FileList | undefined} */
  let files;

  /** @type {ImageAsset | undefined} */
  let selectedImage;

  /** @type {boolean} */
  let uploaded = false;

  /** @type {string | undefined} */
  let uploadError;

  /**
   * Asynchronously retrieves the dimensions (width and height) of an image file.
   *
   * @param {File} file - A File object, expected to be an image.
   * @returns {Promise<[number, number]>} A promise that resolves to an image's width and height in pixels.
   * @throws {Error} If the file is not an image or if reading/loading the image fails.
   */
  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        return reject(new Error('Not an image file'));
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          resolve([img.width, img.height]);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handles the file input change event, extracts image metadata,
   * and triggers a preview callback with a temporary ImageAsset.
   *
   * @param {Event} event - The change event from a file input element.
   * @returns {Promise<void>} A promise that resolves when the image has been processed.
   */
  const handleFileChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      const [width, height] = await getImageDimensions(file);
      const tempImage = {
        filename: file.name,
        filesize: file.size,
        imageurl: URL.createObjectURL(file),
        mimetype: file.type,
        metadata: {
          origfile: file.name,
          filesize: file.size,
          mimetype: file.type,
          imageinfo: {
            width,
            height
          }
        }
      };

      onPreview(tempImage);
    }
  };

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  /**
   * Handles the asset upload process.
   * @returns {Promise<void>}
   */
  const onUpload = async () => {
    uploading = true;

    try {
      const form = new FormData();
      form.append('file', files[0]);

      const uploadResponse = await AssetsService.uploadFile(files[0].name, files[0].type, form);

      if (uploadResponse?.status >= 400) {
        notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }), 'error');

        uploadError = uploadResponse.message;
        uploading = false;

        return;
      }

      const { hash, filesize, filename, metadata } = uploadResponse;

      const data = {
        hash,
        filesize,
        filename,
        metadata,
        inline: false,
        mimetype: metadata.mimetype,
        rulename: filename
      };

      const response = await AssetsService.addImage(data);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: filename } }), 'error');

        uploadError = response.message;
        uploading = false;

        return;
      }

      notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: filename } }));

      selectedImage = response;
      uploaded = true;
      uploadError = undefined;

      onChange(selectedImage);
    } finally {
      uploading = false;
    }
  };

  const onRemove = () => {
    selectedImage = undefined;
    fileInput.value = '';

    files = undefined;
    uploaded = false;
    uploadError = undefined;

    onPreview(undefined);
  };

  $: url = selectedImage?.imageurl || (files?.length && URL.createObjectURL(files[0]));
</script>

<InputGroup {id} class={className}>
  <Input
    id="file-{id}"
    type="file"
    accept="image/*"
    disabled={uploading || uploaded}
    invalid={!!uploadError}
    bind:files
    bind:inner={fileInput}
    on:change={handleFileChange}
  />
  {#if files?.length}
    <Button
      size="sm"
      class="px-2 {uploaded ? 'text-success' : ''}"
      disabled={uploading || uploaded}
      on:click={onUpload}
    >
      {#if uploading}
        <Spinner size="sm" color="secondary" />
        <span class={hideMobileLabel}>
          {$format('label.UPLOADING')}
        </span>
      {:else if uploaded}
        <Icon name="check-circle" />
        <span class={hideMobileLabel}>
          {$format('label.UPLOADED')}
        </span>
      {:else}
        <Icon name="upload" />
        <span class={hideMobileLabel}>
          {$format('label.UPLOAD')}
        </span>
      {/if}
    </Button>
    <Button size="sm" class="px-2 text-danger" disabled={uploading} on:click={onRemove}>
      <Icon name="trash-2" />
      <span class={hideMobileLabel}>
        {$format('label.REMOVE')}
      </span>
    </Button>
  {/if}
</InputGroup>
{#if uploadError}
  <Tooltip target={id} placement="bottom">
    {uploadError}
  </Tooltip>
{/if}
